import type {
	UpdateDataFn,
	TabData,
	FetchTabDataFn,
} from '@atlassian/jira-home/src/common/types/fetch.tsx';
import type { RouteResourceUpdater } from '@atlassian/react-resource-router';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, jira/export/restrict-types-re-export
export type { UpdateDataFn };

type State = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	update: (getNewData: RouteResourceUpdater<any>) => void;
};

/**
 * Updates state of the the given tabName.
 * updateFn accepts the tabs current state and returns the new state.
 * @param {*} state
 * @param {*} tabName
 * @param {*} updateFn
 */
export const updateTabRouteResource = (
	state: State,
	tabName: string,
	updateFn: UpdateDataFn,
): void => {
	state.update((currentData) => {
		const currentTabData = currentData && currentData.tabs && currentData.tabs[tabName];
		const updatedData = updateFn(currentTabData);
		return {
			...currentData,
			tabs: {
				...((currentData && currentData.tabs) || null),
				[tabName]: updatedData,
			},
		};
	});
};

/**
 * Creates a function which updates the current tabs state with the latest version.
 * @param {*} fetchData
 */
export const generateUpdateTabDataFn = async (fn: () => Promise<TabData>) => {
	const data = await fn();
	return () => data;
};

/**
 * This is responsible for generating a tab state update function.
 *
 * All of the work done at this level to simplify the client's api.
 *
 * The reason for the apparent weirdness of this function is that fetching more data is async, while RouteRourse#update is synchronous.
 *
 * The returned function, if invoked without any arguments, will call the asynchronous fetchData function and use that data to update the Tab state.
 * However, update also accepts function argument, which gives the calling code access to the current state and allows for updates in place.
 *
 * Its worth nothing that any updates that occurs should also be persisted serverside, as refetching the data will cause the local state to be overwritten.
 * @param {*} state
 * @param {*} tabName
 * @param {*} fetchData
 */
export const generateUpdateTabStateFn =
	(state: State, tabName: string, fetchData: FetchTabDataFn) =>
	async (updateDataFn?: UpdateDataFn): Promise<void> => {
		const updateTabDataFn = updateDataFn || (await generateUpdateTabDataFn(() => fetchData()));
		updateTabRouteResource(state, tabName, updateTabDataFn);
	};
